import { createFileRoute, notFound } from '@tanstack/react-router'
import { getTranslations } from '@/i18n/translator'
import { pageHead, SITE_URL } from '@/lib/seo'
import { breadcrumbSchema, homeBreadcrumbItem } from '@/lib/structured-data'
import { isFeatureKey } from '@/features/solution-page/data/features'
import { FeatureHero } from '@/features/solution-page/components/feature-hero'
import { FeatureVideo } from '@/features/solution-page/components/feature-video'
import { HowItWorks } from '@/features/solution-page/components/how-it-works-section'
import { FeatureExpertBand } from '@/features/solution-page/components/firefighter-section'
import { ClientResultsSection } from '@/features/solution-page/components/client-results-section'
import { FaqSection } from '@/features/landing-page/components/faq-section'
import { SimulatorSection } from '@/features/solution-page/components/simulator-section'
import { FinalCtaSection } from '@/features/landing-page/components/final-cta-section'

// Every feature FAQ uses the same five uniform item keys, so one array serves
// all pages — the per-feature copy lives in messages under the namespace below.
const FAQ_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5'] as const

export const Route = createFileRoute(
  '/$locale/solutions/people-security/$feature',
)({
  loader: async ({ params }) => {
    if (!isFeatureKey(params.feature)) throw notFound()
    const t = await getTranslations({
      locale: params.locale,
      namespace: `solutions.people.features.${params.feature}`,
    })
    return {
      feature: params.feature,
      title: t('metaTitle'),
      description: t('metaDescription'),
    }
  },
  head: ({ loaderData, params }) => {
    const path = `/solutions/people-security/${params.feature}`
    return pageHead({
      locale: params.locale,
      path,
      title: loaderData?.title ?? '',
      description: loaderData?.description ?? '',
      jsonLd: breadcrumbSchema([
        homeBreadcrumbItem(params.locale),
        { name: loaderData?.title ?? '', url: `${SITE_URL}/${params.locale}${path}` },
      ]),
    })
  },
  component: FeaturePage,
})

function FeaturePage() {
  const { feature } = Route.useLoaderData()
  return (
    <>
      <FeatureHero feature={feature} category="people" />
      <FeatureVideo feature={feature} category="people" />
      <HowItWorks feature={feature} category="people" />
      <FeatureExpertBand feature={feature} />
      <ClientResultsSection feature={feature} category="people" />
      <FaqSection
        namespace={`solutions.people.features.${feature}.faq`}
        items={FAQ_KEYS}
      />
      <FinalCtaSection />
    </>
  )
}
